import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Image from './Image';
import left_image1 from '../../assets/images/left_image1.png';
import right_image2 from '../../assets/images/right_image2.png';
import { useNavigate } from 'react-router-dom';

export default function ImageLayout() {
  const [mounted, setMounted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  if (!mounted) return null;

  return (
    <div className="bg-gradient-to-r from-pink-200 to-blue-300 w-full p-4 flex items-center justify-center">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center text-center my-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-bold text-blue-900 mb-6 max-w-4xl"
          >
            Coding Classes for Kids and Teens, Simplified with Real-Time AI
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              My Code Dragon offers AI-powered online coding courses and classes
              for kids and teens. Learn Python, R, SQL, JavaScript, HTML, and
              Typing with an embedded code editor, real-time AI assistance, and
              hands-on coding activities that turn screen time into skill time.
              Students get clear, actionable feedback that helps keep
              frustration low and engagement high, no setup required. Log in and
              start coding.
            </p>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onClick={() => {
              navigate('/signup?subscriptionType=1');
            }}
            className="mt-8 px-8 py-4 bg-[#FF4D4D] text-white text-lg font-semibold rounded-full 
              hover:bg-[#FF3333] transition-colors duration-300 shadow-lg"
          >
            GET STARTED FOR FREE
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 w-full max-w-4xl"
          >
            <video
              ref={videoRef}
              className="w-full min-h-[400px] max-h-[600px] rounded-xl shadow-2xl"
              controls
              loop
              playsInline
              preload="auto"
            >
              <source
                src="https://my-code-dragon.s3.eu-north-1.amazonaws.com/public-assets/My+dragon+code+v1.0.4+(1).mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </motion.div>
        </div>

        <motion.div
          variants={imageVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col md:flex-row items-center justify-center gap-8 mt-12"
        >
          <div className="w-full md:w-1/3">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <Image
                src={left_image1}
                alt="Interactive coding lessons"
                width={300}
                height={300}
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>

          <div className="w-full md:w-1/3">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <Image
                src={right_image2}
                alt="Real-time AI support"
                width={300}
                height={300}
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
